import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { FilePen, Loader2, Plus, Send, Ship, X as XIcon } from "lucide-react";
import { api } from "@/lib/api";
import { useTelecomAccess } from "@/hooks/useTelecomAccess";
import { SectorNoHabilitadoPanel } from "@/components/SectorNoHabilitadoPanel";
import { PaisesPicker } from "@/components/PaisesPicker";
import {
  CapturaDocumentos,
  subirDocumentosCapturados,
  type DocumentoCapturado,
} from "@/components/CapturaDocumentos";
import {
  Alert,
  Button,
  Card,
  DataField,
  FormField,
  Input,
  PageContainer,
  PageHeader,
  Select,
  Textarea,
} from "@/components/ui";

/**
 * Alta de una Corrección de Carta Aduana (#30) — patrón DICE / DEBE DECIR
 * sobre una Carta Aduana EMITIDA (RN-13/RN-14/RN-18 del diseño funcional).
 *
 * Ajuste 2026-07-13: los campos de catálogo (países, fracciones, aduanas,
 * laboratorio, normas) usan los MISMOS selectores que el alta de la solicitud
 * — nada de texto libre cuando existe catálogo oficial. El DEBE DECIR se
 * serializa al mismo formato textual que el snapshot DICE del backend, por lo
 * que guardado y consulta no cambian.
 */

interface CartaEmitida {
  id: string;
  folio: string;
  productoNombre: string;
  marca: string;
  productoModelo: string;
  fechaCreacion: string;
}

interface CartaAduanaDetalle {
  normas: string[];
  series: string[];
  sinSerie: boolean;
  justificacionSerie: string | null;
  accesorios: string;
  paisesOrigen: string[];
  paisesProcedencia: string[];
  fracciones: string[];
  numMuestras: number;
  aduanas: { numero: string; nombre: string }[];
  laboratorio: string;
}

interface Norma { id: string; codigo: string; titulo: string; tipo: string; }
interface Aduana { id: string; numero: string; nombre: string; }
interface Laboratorio { id: string; razonSocial: string; nombreCorto: string; }
interface Pais { id: string; codigo: string; nombre: string; }

const CONTEXTO_TIPO = "SOLICITUD_TELECOM";

/** Tipo de editor por campo corregible. */
type TipoCampo = "texto" | "numero" | "paises" | "fracciones" | "aduanas" | "laboratorio" | "normas";

const CAMPOS: { key: string; label: string; tipo: TipoCampo }[] = [
  { key: "PRODUCTO_NOMBRE", label: "Nombre del producto", tipo: "texto" },
  { key: "MARCA", label: "Marca", tipo: "texto" },
  { key: "MODELO", label: "Modelo", tipo: "texto" },
  { key: "SERIES", label: "Número(s) de serie", tipo: "texto" },
  { key: "ACCESORIOS", label: "Accesorios", tipo: "texto" },
  { key: "PAISES_ORIGEN", label: "País(es) de origen", tipo: "paises" },
  { key: "PAISES_PROCEDENCIA", label: "País(es) de procedencia", tipo: "paises" },
  { key: "FRACCIONES", label: "Fracción(es) arancelaria(s)", tipo: "fracciones" },
  { key: "NUM_MUESTRAS", label: "Número de muestras", tipo: "numero" },
  { key: "ADUANAS", label: "Aduana(s)", tipo: "aduanas" },
  { key: "LABORATORIO", label: "Laboratorio", tipo: "laboratorio" },
  { key: "NORMAS", label: "Norma(s) / DT", tipo: "normas" },
];

/** Valor capturado del DEBE DECIR, tipado por clase de campo. */
type ValorCampo =
  | { tipo: "texto" | "numero"; texto: string }
  | { tipo: "paises"; ids: string[] }
  | { tipo: "fracciones"; lista: string[] }
  | { tipo: "aduanas"; ids: string[] }
  | { tipo: "laboratorio"; id: string }
  | { tipo: "normas"; ids: string[] };

export function CorreccionCartaAduanaAltaPage() {
  const access = useTelecomAccess();
  const navigate = useNavigate();

  const [cartaSel, setCartaSel] = useState<CartaEmitida | null>(null);
  /** key → valor DEBE DECIR; presencia de la key = campo marcado para corregir. */
  const [cambios, setCambios] = useState<Record<string, ValorCampo>>({});
  const [fraccionInput, setFraccionInput] = useState("");
  const [fraccionError, setFraccionError] = useState<string | null>(null);
  const [motivo, setMotivo] = useState("");
  const [docs, setDocs] = useState<DocumentoCapturado[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [solicitudCreada, setSolicitudCreada] = useState<string | null>(null);

  // ----- Catálogos (los mismos del alta de Carta Aduana) -----------------------
  const qEmitidas = useQuery<CartaEmitida[]>({
    queryKey: ["cartas-aduana-emitidas"],
    queryFn: async () =>
      (await api.get<CartaEmitida[]>("/api/sector/telecom/solicitudes/carta-aduana/emitidas")).data,
    enabled: access.allowed,
  });

  const qDetalle = useQuery<CartaAduanaDetalle>({
    queryKey: ["carta-aduana-detalle", cartaSel?.id],
    queryFn: async () =>
      (await api.get<CartaAduanaDetalle>(
        `/api/sector/telecom/solicitudes/${cartaSel!.id}/carta-aduana`,
      )).data,
    enabled: access.allowed && !!cartaSel,
  });

  const qCatalogos = useQuery<{ normas: Norma[]; aduanas: Aduana[] }>({
    queryKey: ["telecom-carta-aduana-catalogos"],
    queryFn: async () =>
      (await api.get<{ normas: Norma[]; aduanas: Aduana[] }>(
        "/api/sector/telecom/carta-aduana/catalogos",
      )).data,
    enabled: access.allowed,
  });

  const qPaises = useQuery<Pais[]>({
    queryKey: ["telecom-paises"],
    queryFn: async () => (await api.get<Pais[]>("/api/sector/telecom/paises")).data,
    enabled: access.allowed,
  });

  const d = qDetalle.data ?? null;
  const catalogoNormas = qCatalogos.data?.normas ?? [];
  const catalogoAduanas = qCatalogos.data?.aduanas ?? [];
  const catalogoPaises = qPaises.data ?? [];

  // Normas para filtrar laboratorios (RN-07): las del DEBE DECIR si se está
  // corrigiendo NORMAS, o las de la carta original.
  const normaIdsParaLab = useMemo(() => {
    const cambioNormas = cambios["NORMAS"];
    if (cambioNormas?.tipo === "normas" && cambioNormas.ids.length > 0) return cambioNormas.ids;
    if (!d) return [] as string[];
    return catalogoNormas.filter((n) => d.normas.includes(n.codigo)).map((n) => n.id);
  }, [cambios, d, catalogoNormas]);

  const qLabs = useQuery<{ laboratorios: Laboratorio[]; alcanceCompleto: boolean }>({
    queryKey: ["telecom-carta-aduana-labs", [...normaIdsParaLab].sort().join(",")],
    queryFn: async () =>
      (await api.get<{ laboratorios: Laboratorio[]; alcanceCompleto: boolean }>(
        `/api/sector/telecom/carta-aduana/laboratorios?normas=${normaIdsParaLab.join(",")}`,
      )).data,
    enabled: access.allowed && normaIdsParaLab.length > 0,
  });

  // ----- DICE textual (mismo formato que el snapshot del backend) --------------
  const dice = useMemo<Record<string, string>>(() => {
    if (!cartaSel || !d) return {} as Record<string, string>;
    return {
      PRODUCTO_NOMBRE: cartaSel.productoNombre,
      MARCA: cartaSel.marca,
      MODELO: cartaSel.productoModelo,
      SERIES: d.sinSerie
        ? `Sin número de serie — ${d.justificacionSerie ?? ""}`
        : d.series.join(", "),
      ACCESORIOS: d.accesorios,
      PAISES_ORIGEN: d.paisesOrigen.join(", "),
      PAISES_PROCEDENCIA: d.paisesProcedencia.join(", "),
      FRACCIONES: d.fracciones.join(", "),
      NUM_MUESTRAS: String(d.numMuestras),
      ADUANAS: d.aduanas.map((a) => `${a.nombre} (No. ${a.numero})`).join(" · "),
      LABORATORIO: d.laboratorio,
      NORMAS: d.normas.join(", "),
    };
  }, [cartaSel, d]);

  // ----- Alta/baja de campos con prellenado desde el DICE ----------------------
  const valorInicial = (tipo: TipoCampo): ValorCampo => {
    switch (tipo) {
      case "paises":
        // Sin prellenado ni copia: es una corrección — el usuario captura
        // directamente los países correctos con el selector estándar.
        return { tipo: "paises", ids: [] };
      case "fracciones":
        return { tipo: "fracciones", lista: d ? [...d.fracciones] : [] };
      case "aduanas":
        return {
          tipo: "aduanas",
          ids: d
            ? catalogoAduanas.filter((a) => d.aduanas.some((x) => x.numero === a.numero)).map((a) => a.id)
            : [],
        };
      case "normas":
        return {
          tipo: "normas",
          ids: d ? catalogoNormas.filter((n) => d.normas.includes(n.codigo)).map((n) => n.id) : [],
        };
      case "laboratorio":
        return { tipo: "laboratorio", id: "" };
      default:
        return { tipo, texto: "" };
    }
  };

  const toggleCampo = (key: string, tipo: TipoCampo) =>
    setCambios((prev) => {
      const next = { ...prev };
      if (key in next) delete next[key];
      else next[key] = valorInicial(tipo);
      return next;
    });

  const setValor = (key: string, v: ValorCampo) =>
    setCambios((prev) => ({ ...prev, [key]: v }));

  const agregarFraccion = () => {
    const f = fraccionInput.trim();
    if (!/^\d{8}$/.test(f)) {
      setFraccionError("La fracción debe tener 8 dígitos.");
      return;
    }
    const actual = cambios["FRACCIONES"];
    const lista = actual?.tipo === "fracciones" ? actual.lista : [];
    if (lista.includes(f)) {
      setFraccionError("Esa fracción ya está en la lista.");
      return;
    }
    setValor("FRACCIONES", { tipo: "fracciones", lista: [...lista, f] });
    setFraccionInput("");
    setFraccionError(null);
  };

  // ----- Validación y serialización (mismo formato textual que el DICE) --------
  const valorCompleto = (v: ValorCampo): boolean => {
    switch (v.tipo) {
      case "texto":
      case "numero": return v.texto.trim() !== "";
      case "paises":
      case "aduanas":
      case "normas": return v.ids.length > 0;
      case "fracciones": return v.lista.length > 0;
      case "laboratorio": return v.id !== "";
    }
  };

  const serializar = (v: ValorCampo): string => {
    switch (v.tipo) {
      case "texto":
      case "numero":
        return v.texto.trim();
      case "paises":
        return catalogoPaises
          .filter((p) => v.ids.includes(p.id))
          .map((p) => p.nombre)
          .sort((a, b) => a.localeCompare(b))
          .join(", ");
      case "fracciones":
        return v.lista.join(", ");
      case "aduanas":
        return catalogoAduanas
          .filter((a) => v.ids.includes(a.id))
          .map((a) => `${a.nombre} (No. ${a.numero})`)
          .join(" · ");
      case "laboratorio":
        return qLabs.data?.laboratorios.find((l) => l.id === v.id)?.razonSocial ?? "";
      case "normas":
        return catalogoNormas
          .filter((n) => v.ids.includes(n.id))
          .map((n) => n.codigo)
          .sort()
          .join(", ");
    }
  };

  const entradas = Object.entries(cambios);
  const valido =
    !!cartaSel &&
    entradas.length > 0 &&
    entradas.every(([, v]) => valorCompleto(v)) &&
    motivo.trim() !== "";

  const enviar = async () => {
    if (!valido || !cartaSel) return;
    setError(null);
    setEnviando(true);
    try {
      let id = solicitudCreada;
      if (!id) {
        id = (
          await api.post<{ id: string }>(
            "/api/sector/telecom/solicitudes/carta-aduana/correccion",
            {
              cartaOriginalId: cartaSel.id,
              cambios: entradas.map(([campo, v]) => ({ campo, debeDecir: serializar(v) })),
              motivo: motivo.trim(),
            },
          )
        ).data.id;
        setSolicitudCreada(id);
      }
      const fallidos = docs.length
        ? await subirDocumentosCapturados(docs, setDocs, CONTEXTO_TIPO, id)
        : 0;
      if (fallidos > 0) {
        setError(
          `La solicitud se registró, pero ${fallidos} documento(s) no se pudieron subir. ` +
            `Reintenta los marcados en rojo o continúa y adjúntalos desde el detalle.`,
        );
        setEnviando(false);
        return;
      }
      navigate(`/sector/telecom/solicitudes/${id}/success`);
    } catch (e: any) {
      setError(
        e?.response?.data?.detail ?? e?.response?.data?.message ?? "No se pudo registrar la solicitud.",
      );
      setEnviando(false);
    }
  };

  // ----- Editor del DEBE DECIR por tipo de campo --------------------------------
  const renderEditor = (key: string) => {
    const v = cambios[key];
    if (!v) return null;
    switch (v.tipo) {
      case "texto":
      case "numero":
        return (
          <FormField label="DEBE DECIR" required className="mt-2">
            <Input
              type={v.tipo === "numero" ? "number" : "text"}
              min={v.tipo === "numero" ? 1 : undefined}
              value={v.texto}
              onChange={(e) => setValor(key, { tipo: v.tipo, texto: e.target.value })}
              placeholder="Valor correcto"
            />
          </FormField>
        );
      case "paises":
        return (
          <div className="mt-2">
            <PaisesPicker
              label="DEBE DECIR"
              required
              opciones={catalogoPaises}
              seleccion={v.ids}
              onChange={(ids) => setValor(key, { tipo: "paises", ids })}
            />
          </div>
        );
      case "fracciones":
        return (
          <FormField
            label="DEBE DECIR"
            required
            hint="8 dígitos por fracción; puedes registrar varias."
            error={fraccionError ?? undefined}
            className="mt-2"
          >
            <div className="space-y-2">
              <div className="flex gap-2">
                <Input
                  value={fraccionInput}
                  onChange={(e) => { setFraccionInput(e.target.value); setFraccionError(null); }}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); agregarFraccion(); } }}
                  placeholder="p. ej. 98060001"
                />
                <Button type="button" variant="secondary" icon={<Plus className="h-4 w-4" />} onClick={agregarFraccion}>
                  Agregar
                </Button>
              </div>
              {v.lista.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {v.lista.map((f) => (
                    <span key={f} className="inline-flex items-center gap-1 rounded-full border border-edge bg-surface-2 px-2 py-0.5 font-mono text-xs">
                      {f}
                      <button
                        type="button"
                        title={`Quitar ${f}`}
                        onClick={() => setValor(key, { tipo: "fracciones", lista: v.lista.filter((x) => x !== f) })}
                        className="text-fg-faint hover:text-estado-err"
                      >
                        <XIcon className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </FormField>
        );
      case "aduanas":
        return (
          <FormField label="DEBE DECIR" required hint="La aduana se registra con nombre y número." className="mt-2">
            <div className="grid gap-2 sm:grid-cols-2">
              {catalogoAduanas.map((a) => (
                <label key={a.id} className="flex cursor-pointer items-center gap-2 rounded-md border border-border p-2 text-sm hover:bg-surface-2">
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    checked={v.ids.includes(a.id)}
                    onChange={() =>
                      setValor(key, {
                        tipo: "aduanas",
                        ids: v.ids.includes(a.id) ? v.ids.filter((x) => x !== a.id) : [...v.ids, a.id],
                      })
                    }
                  />
                  <span>
                    {a.nombre} <span className="text-xs text-fg-subtle">(No. {a.numero})</span>
                  </span>
                </label>
              ))}
            </div>
          </FormField>
        );
      case "laboratorio":
        return (
          <FormField
            label="DEBE DECIR"
            required
            className="mt-2"
            hint={normaIdsParaLab.length === 0 ? "No hay normas para filtrar laboratorios." : undefined}
          >
            <Select
              value={v.id}
              onChange={(e) => setValor(key, { tipo: "laboratorio", id: e.target.value })}
              disabled={qLabs.isLoading || normaIdsParaLab.length === 0}
            >
              <option value="">— Selecciona —</option>
              {(qLabs.data?.laboratorios ?? []).map((l) => (
                <option key={l.id} value={l.id}>{l.razonSocial}</option>
              ))}
            </Select>
            {qLabs.data && !qLabs.data.alcanceCompleto && (
              <p className="mt-1 text-xs text-estado-warn">
                Ningún laboratorio tiene alcance registrado para todas las normas; se muestran todos los activos.
              </p>
            )}
          </FormField>
        );
      case "normas":
        return (
          <FormField label="DEBE DECIR" required className="mt-2">
            <div className="grid gap-2 sm:grid-cols-2">
              {catalogoNormas.map((n) => (
                <label key={n.id} className="flex cursor-pointer items-start gap-2 rounded-md border border-border p-2 text-sm hover:bg-surface-2">
                  <input
                    type="checkbox"
                    className="mt-0.5 h-4 w-4"
                    checked={v.ids.includes(n.id)}
                    onChange={() =>
                      setValor(key, {
                        tipo: "normas",
                        ids: v.ids.includes(n.id) ? v.ids.filter((x) => x !== n.id) : [...v.ids, n.id],
                      })
                    }
                  />
                  <span>
                    <span className="font-medium">{n.codigo}</span>
                    <span className="block text-xs text-fg-subtle">{n.titulo}</span>
                  </span>
                </label>
              ))}
            </div>
          </FormField>
        );
    }
  };

  // ----- Guards -----------------------------------------------------------------
  if (access.loading) {
    return (
      <div className="flex items-center justify-center p-12 text-sm text-fg-subtle">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Validando permisos…
      </div>
    );
  }
  if (!access.allowed) {
    return (
      <SectorNoHabilitadoPanel
        sectorNombre="Telecomunicaciones"
        sectorCodigo="TELECOM"
        titularRazonSocial={access.titularRazonSocial}
        sinContexto={access.sinContexto}
      />
    );
  }

  return (
    <PageContainer width="form">
      <PageHeader
        backTo="/telecom/servicios"
        backLabel="Menú de servicios"
        icon={<FilePen className="h-6 w-6" />}
        title="Corrección de Carta Aduana"
        subtitle="Formato NPM-T0037-R12 · Servicio #30 · DICE / DEBE DECIR"
      />

      <Alert variant="info">
        La corrección opera sobre una <strong>Carta Aduana emitida</strong>: indica los campos
        con error, el valor correcto y el motivo. La carta original no se modifica — ambas
        quedan vinculadas en el expediente.
      </Alert>

      {/* Paso 1: seleccionar carta emitida */}
      <Card title="Carta Aduana a corregir">
        {qEmitidas.isLoading ? (
          <div className="flex items-center gap-2 p-2 text-sm text-fg-subtle">
            <Loader2 className="h-4 w-4 animate-spin" /> Cargando cartas emitidas…
          </div>
        ) : (qEmitidas.data ?? []).length === 0 ? (
          <Alert variant="warning">
            No tienes Cartas Aduana emitidas. Solo pueden corregirse cartas cuyo trámite
            ya concluyó (emitidas).
          </Alert>
        ) : (
          <div className="grid gap-2">
            {(qEmitidas.data ?? []).map((c) => (
              <label
                key={c.id}
                className={`flex cursor-pointer items-center gap-3 rounded-md border p-3 text-sm transition ${
                  cartaSel?.id === c.id
                    ? "border-primary bg-primary-tint"
                    : "border-border hover:bg-surface-2"
                }`}
              >
                <input
                  type="radio"
                  name="carta"
                  className="h-4 w-4"
                  checked={cartaSel?.id === c.id}
                  onChange={() => {
                    setCartaSel(c);
                    setCambios({});
                    setFraccionInput("");
                    setFraccionError(null);
                  }}
                />
                <Ship className="h-4 w-4 shrink-0 text-fg-faint" />
                <span>
                  <span className="font-medium">{c.folio}</span>
                  <span className="block text-xs text-fg-subtle">
                    {c.productoNombre} · {c.marca} {c.productoModelo}
                  </span>
                </span>
              </label>
            ))}
          </div>
        )}
      </Card>

      {/* Paso 2: DICE / DEBE DECIR */}
      {cartaSel && (
        <Card title={`Cambios solicitados — ${cartaSel.folio}`}>
          {qDetalle.isLoading ? (
            <div className="flex items-center gap-2 p-2 text-sm text-fg-subtle">
              <Loader2 className="h-4 w-4 animate-spin" /> Cargando datos de la carta…
            </div>
          ) : (
            <>
              <p className="mb-3 text-sm text-fg-subtle">
                Marca los campos con error. El valor actual (DICE) es informativo; captura el
                valor correcto (DEBE DECIR) con los mismos selectores de la solicitud. En los
                campos de catálogo (salvo países) el DEBE DECIR se prellena con los valores
                actuales para que solo modifiques lo necesario.
              </p>
              <div className="grid gap-2">
                {CAMPOS.map(({ key, label, tipo }) => {
                  const marcado = key in cambios;
                  return (
                    <div
                      key={key}
                      className={`rounded-md border p-3 ${marcado ? "border-primary/40 bg-primary-tint/40" : "border-border"}`}
                    >
                      <label className="flex cursor-pointer items-start gap-2 text-sm">
                        <input
                          type="checkbox"
                          className="mt-0.5 h-4 w-4"
                          checked={marcado}
                          onChange={() => toggleCampo(key, tipo)}
                        />
                        <span className="min-w-0 flex-1">
                          <span className="font-medium">{label}</span>
                          <span className="mt-0.5 block text-xs text-fg-subtle">
                            <span className="font-semibold">DICE:</span> {dice[key] || "—"}
                          </span>
                        </span>
                      </label>
                      {marcado && renderEditor(key)}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </Card>
      )}

      {/* Paso 3: motivo y soporte */}
      {cartaSel && (
        <Card title="Motivo y documentos de soporte">
          <FormField
            label="Motivo de la corrección"
            required
            hint="Explica el origen del error y el sustento del valor correcto."
          >
            <Textarea
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="p. ej. El error se originó al transcribir la factura comercial; se anexa la factura corregida."
            />
          </FormField>
          <div className="mt-4">
            <CapturaDocumentos
              tag="TELECOM_CORRECCION_CA"
              titulo="Documentos de soporte"
              ayuda="Adjunta la Carta Aduana original y la evidencia del dato correcto (en español)."
              docs={docs}
              onChange={setDocs}
              disabled={enviando}
              mostrarRequeridos
            />
          </div>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <DataField label="Carta original" value={cartaSel.folio} />
            <DataField label="Campos a corregir" value={String(entradas.length)} />
          </div>
        </Card>
      )}

      {error && <Alert variant="error">{error}</Alert>}

      <div className="flex justify-end gap-2">
        <Link to="/telecom/servicios">
          <Button variant="secondary" disabled={enviando}>Regresar</Button>
        </Link>
        <Button
          variant="success"
          icon={<Send className="h-4 w-4" />}
          loading={enviando}
          disabled={!valido || enviando}
          onClick={enviar}
        >
          Enviar solicitud
        </Button>
      </div>
    </PageContainer>
  );
}
